import {
  Box,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import useRfidGroupByRfidId from 'hooks/useRfidGroupByRfidId';
import { useTranslation } from 'next-i18next';
import { useCallback } from 'react';
import type { FC } from 'react';

import RfidGroupsIcon from 'components/Icons/RfidGroupsIcon';
import RfidIcon from 'components/Icons/RfidIcon';
import CustomCheckbox from 'components/MuiCustom/CustomCheckbox';

import type { PublicDatabaseRfid } from 'redux-store/slices/rfids/types';
import type { RfidId } from 'redux-store/slices/ui/types';

export interface RfidGroupsRfidItemProps {
  rfid: PublicDatabaseRfid;
  selectedRfids: RfidId[];
  setselectedRfids: (rfids: RfidId[]) => void;
}

const CustomBox = styled(Box)(({ theme }) => ({
  '&:not(:first-of-type)': {
    borderTop: `1px solid ${theme.vars.palette.divider}`,
  },
  display: 'flex',
  flexDirection: 'row',
  width: '100%',
  gap: theme.spacing(1),
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1, 2),
  cursor: 'pointer',
}));

const RfidGroupsRfidItem: FC<RfidGroupsRfidItemProps> = ({
  rfid,
  selectedRfids,
  setselectedRfids,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const handleCheckboxChange = useCallback(() => {
    if (selectedRfids.includes(rfid.id)) {
      setselectedRfids(selectedRfids.filter(r => r !== rfid.id));
    } else {
      setselectedRfids([...selectedRfids, rfid.id]);
    }
  }, [rfid.id, selectedRfids, setselectedRfids]);

  const rfidGroups = useRfidGroupByRfidId(rfid.id);

  return (
    <CustomBox onClick={handleCheckboxChange}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        width="100%"
      >
        <Box
          display="flex"
          justifyContent="flex-start"
          alignItems="center"
          gap={1}
        >
          <CustomCheckbox
            checked={selectedRfids.includes(rfid.id)}
            onChange={handleCheckboxChange}
            data-testid="select-rfid-checkbox"
          />

          <RfidIcon />
          <Box display="flex" flexDirection="column">
            <Typography data-testid="rfid-name">{rfid.name}</Typography>
            {rfid.name ? (
              <Typography variant="subtitle1" data-testid="rfid-code">
                {t('common:pages.rfid_groups.rfid_code', {
                  rfidCode: rfid.rfid,
                })}
              </Typography>
            ) : null}
          </Box>
        </Box>
        {rfidGroups && rfidGroups.length > 0 && !smallScreen ? (
          <Box
            gap={1}
            maxWidth={200}
            flexDirection="row"
            display="flex"
            alignItems="center"
          >
            <RfidGroupsIcon fontSize="small" sx={{ color: 'grey.500' }} />

            <Typography
              data-testid="device-group-names"
              variant="body1"
              noWrap
              color={theme.vars.palette.grey[500]}
            >
              {rfidGroups.map((group, index) => (
                <span key={group.id}>
                  {group.name}
                  {index < rfidGroups.length - 1 ? ', ' : null}
                </span>
              ))}
            </Typography>
          </Box>
        ) : null}
      </Box>
    </CustomBox>
  );
};

export default RfidGroupsRfidItem;
